import {announce} from '@github-ui/aria-live'
import {sendEvent} from '@github-ui/hydro-analytics'
import {BellIcon, BellSlashIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {useState} from 'react'

import {useUpdateSubscriptionMutation} from '../../mutations/use-update-subscription-mutation'
import {useOverviewRouteData} from './data/use-overview-route-data'
import {SidebarSection} from './SidebarSection'

const DESCRIPTION_ID = 'pull-request-subscription-description'

/**
 * Notifications sidebar section. Lets the viewer subscribe to or unsubscribe from this pull
 * request's notification thread. Hidden for signed-out viewers, who cannot subscribe.
 */
export function SubscriptionSection() {
  const {user, notificationSubscriptionThreadId} = useOverviewRouteData().data

  if (!user || !notificationSubscriptionThreadId) return null

  return (
    <SidebarSection title="Notifications">
      <Subscription subscribed={user?.subscribed ?? false} ignoringRepository={user?.ignoringRepository ?? false} />
    </SidebarSection>
  )
}

function Subscription({
  subscribed: initialSubscribed,
  ignoringRepository,
}: {
  subscribed: boolean
  ignoringRepository: boolean
}) {
  const [subscribed, setSubscribed] = useState(initialSubscribed)
  const [isSubscriptionStatusUpdated, setIsSubscriptionStatusUpdated] = useState(false)

  const {mutate, isPending} = useUpdateSubscriptionMutation({
    onSuccess: newSubscribed => {
      setSubscribed(newSubscribed)
      setIsSubscriptionStatusUpdated(true)
      announce(newSubscribed ? "You're now subscribed to this thread." : "You're now unsubscribed from this thread.")
    },
    onError: () => {
      announce('Something went wrong. Please try again.')
    },
  })

  const toggleSubscription = () => {
    const newSubscribed = !subscribed
    sendEvent('notifications.thread_subscription.toggle', {
      subscriptionState: newSubscribed ? 'SUBSCRIBED' : 'UNSUBSCRIBED',
      threadClass: 'PullRequest',
    })
    mutate(newSubscribed)
  }

  let description: string
  if (ignoringRepository) {
    description = "You're ignoring this repository."
  } else if (isSubscriptionStatusUpdated) {
    description = subscribed ? "You're now subscribed to this thread." : "You're now unsubscribed from this thread."
  } else if (subscribed) {
    description = "You're receiving notifications because you're subscribed to this thread."
  } else {
    description = "You're not receiving notifications from this thread."
  }

  return (
    <div className="width-full d-flex flex-column flex-items-stretch">
      <Button
        aria-describedby={DESCRIPTION_ID}
        leadingVisual={subscribed ? BellSlashIcon : BellIcon}
        onClick={isPending || ignoringRepository ? undefined : toggleSubscription}
        loading={isPending}
        loadingAnnouncement="Updating subscription"
        inactive={ignoringRepository}
        aria-disabled={isPending || ignoringRepository ? true : undefined}
        block
        size="small"
      >
        {subscribed ? 'Unsubscribe' : 'Subscribe'}
      </Button>
      <p id={DESCRIPTION_ID} className="mt-2 mb-0 f6 fgColor-muted">
        {description}
      </p>
    </div>
  )
}
